import classnames from "classnames";
import { ButtonPrimary } from "components/Atoms/Button";
import TextLinkButton from "components/buttons/TextLinkButton";
import { useCredits } from "credits/useCredits";
import { Loading, Panel } from "components/panels";
import { AutoReloadModal } from "credits/AutoReloadModalApp";
import { BuyCreditsModal } from "credits/BuyCreditsModalApp";
import { CreditApi } from "queries/Credit";
import React from "react";
import { formatCurrency } from "utilities/currencyutils";
import { Toggle } from "components/Atoms/Toggle";
import { useClientId, useIsPaid } from "common/SessionContext";

type PanelType = "s1_panel" | "s1_related-feature-panel";

interface Props {
  panelType: PanelType;
  style?: React.CSSProperties;
}

export const CreditBalancePanel = (props: Props) => {
  const isPaid = useIsPaid();
  const clientId = useClientId();
  const credits = useCredits();
  const creditAccountQuery = CreditApi.GetCreditAccountByClient.useQuery(
    { clientId },
    { enabled: isPaid }
  );
  const [showAutoReloadModal, setShowAutoReloadModal] = React.useState(false);
  const [showBuyCreditsModal, setShowBuyCreditsModal] = React.useState(false);

  const account = creditAccountQuery.data?.data;

  const closeAutoReloadModal = () => {
    setShowAutoReloadModal(false);
  };

  const isLowBalance = credits != null
    && credits < serverReference.constants.credit.lowBalanceWarningThreshold;

  return <>
    <Panel
      panelClassName={props.panelType}
      title={RESX.Credit.CreditBalance}
      style={props.style}
    >
      <div className="s1_gray margin-bottom">Your credit balance will be consumed with AI data import and Peppol usage.</div>

      <div className="credit-balance-panel">
        <div className={classnames("credit-balance-panel-card balance-card", { "low-balance": isLowBalance, "white-text": isLowBalance })}>
          {credits == null
            ? <Loading />
            : <h2>{formatCurrency(credits, "eur", serverReference.culture.uiLocale, { hideCents: credits > 1000 })}</h2>}
          <div>{isLowBalance ? "Remaining balance is low" : RESX.Credit.RemainingBalance}</div>
        </div>

        {isPaid
          ? <div className="credit-balance-panel-right">
            <div className="credit-balance-panel-card auto-reload-card">
              <div className="auto-reload-checkbox">
                {account == null
                  ? <Loading />
                  : <Toggle
                    variant="icons"
                    value={account.autoReloadEnabled}
                    onChange={() => setShowAutoReloadModal(true)}
                  />}
              </div>
              <div className="grow1 flex-column gap-small s1_small">
                {account == null
                  ? <Loading />
                  : <>
                    <div>
                      {account.autoReloadEnabled
                        ? <>Auto reload is <strong>enabled</strong>. We will reload to <strong>{formatCurrency(account.autoReloadGoalAmount, "eur", serverReference.culture.uiLocale, { hideCents: true })}</strong> when the balance drops below <strong>{formatCurrency(account.autoReloadTriggerAmount, "eur", serverReference.culture.uiLocale, { hideCents: true })}</strong>.</>
                        : <>Auto reload is <strong>disabled</strong>. Enable auto reload to avoid interruptions when credits are fully spent. </>}
                    </div>
                    <div>
                      <TextLinkButton
                        onClick={() => setShowAutoReloadModal(true)}
                        style={{ padding: 0 }}
                      >
                        Configure auto reload
                      </TextLinkButton>
                    </div>
                  </>}
              </div>
            </div>

            <div className="buy-credits-wrapper">
              <ButtonPrimary label={RESX.Credit.BuyCredits} onClick={() => setShowBuyCreditsModal(true)} />
            </div>
          </div>
          : <div className="credit-balance-panel-card" style={{ alignSelf: "flex-start" }}>
            Need more credits? Contact support.
          </div>}
      </div>
    </Panel>

    {isPaid && showAutoReloadModal && <AutoReloadModal
      onClose={closeAutoReloadModal}
    />}
    {isPaid && showBuyCreditsModal && <BuyCreditsModal onClose={() => setShowBuyCreditsModal(false)} />}
  </>;
};
